// Progress management module for PPT Hero game
export default class ProgressManager {
  constructor(scene) {
    this.scene = scene;
  }

  // Load round progress from localStorage
  loadRoundProgress() {
    try {
      const savedProgress = localStorage.getItem('pptHeroRoundProgress');
      if (savedProgress) {
        this.scene.roundProgress = JSON.parse(savedProgress);
        console.log('📊 Loaded round progress:', this.scene.roundProgress);
      } else {
        this.scene.roundProgress = {};
        console.log('📊 No saved progress found, starting fresh');
      }
    } catch (error) {
      console.error('Error loading round progress:', error);
      this.scene.roundProgress = {};
    }
  }

  // Save round progress to localStorage
  saveRoundProgress() {
    try {
      localStorage.setItem('pptHeroRoundProgress', JSON.stringify(this.scene.roundProgress));
      console.log('📊 Saved round progress:', this.scene.roundProgress);
    } catch (error) {
      console.error('Error saving round progress:', error);
    }
  }

  // Update round progress data
  updateRoundProgress(roundId, levelIndex, completed = false, score = 0) {
    if (!this.scene.roundProgress[roundId]) {
      this.scene.roundProgress[roundId] = {
        levels: {},
        completed: false,
        bestScore: 0,
        totalScore: 0,
        attempts: 0
      };
    }

    const round = this.scene.roundProgress[roundId];
    round.attempts++;
    
    if (completed) {
      round.levels[levelIndex] = {
        completed: true,
        score: score,
        completedAt: Date.now()
      };
      
      round.totalScore += score;
      if (score > round.bestScore) {
        round.bestScore = score;
      }
      
      // Check if all levels in this round are completed
      const roundData = this.scene.getRoundsDefinition().find(r => r.id === roundId);
      if (roundData && roundData.levels) {
        const allLevelsCompleted = roundData.levels.every(levelId => 
          round.levels[levelId] && round.levels[levelId].completed
        );
        if (allLevelsCompleted) {
          round.completed = true;
          round.completedAt = Date.now();
        }
      }
    }

    this.saveRoundProgress();
    console.log(`📊 Updated progress for ${roundId} level ${levelIndex}:`, round);
  }

  // Update rounds sidebar display
  updateRoundsSidebar() {
    if (!this.scene.roundItems || !Array.isArray(this.scene.roundItems)) return;
    
    this.scene.roundItems.forEach((item, index) => {
      if (!item.roundData) return;
      
      const roundId = item.roundData.id;
      const progress = this.scene.roundProgress[roundId];
      
      if (progress) {
        // Update completion status - check if item has required methods
        if (progress.completed) {
          if (item.setTexture && typeof item.setTexture === 'function') {
            item.setTexture('round-completed');
          }
          if (item.setFillStyle && typeof item.setFillStyle === 'function') {
            item.setFillStyle(0x00ff00, 0.8);
          }
        } else if (progress.attempts > 0) {
          if (item.setTexture && typeof item.setTexture === 'function') {
            item.setTexture('round-started');
          }
          if (item.setFillStyle && typeof item.setFillStyle === 'function') {
            item.setFillStyle(0xffff00, 0.8);
          }
        } else {
          if (item.setTexture && typeof item.setTexture === 'function') {
            item.setTexture('round-available');
          }
          if (item.setFillStyle && typeof item.setFillStyle === 'function') {
            item.setFillStyle(0xffffff, 1.0);
          }
        }
        
        // Update score display if available
        if (item.scoreText && progress.totalScore > 0) {
          item.scoreText.setText(`Score: ${progress.totalScore}`);
        }
      }
    });
  }

  // Get progress for a specific round
  getRoundProgress(roundId) {
    return this.scene.roundProgress[roundId] || null;
  }

  // Check if a round is completed
  isRoundCompleted(roundId) {
    const progress = this.scene.roundProgress[roundId];
    return progress ? progress.completed : false;
  }

  // Get total score across all rounds
  getTotalScore() {
    let total = 0;
    Object.values(this.scene.roundProgress).forEach(round => {
      total += round.totalScore || 0;
    });
    return total;
  }

  // Reset all progress
  resetAllProgress() {
    this.scene.roundProgress = {};
    this.saveRoundProgress();
    this.updateRoundsSidebar();
    console.log('📊 Reset all progress');
  }

  // Export progress data
  exportProgress() {
    return {
      roundProgress: this.scene.roundProgress,
      totalScore: this.getTotalScore(),
      exportDate: new Date().toISOString()
    };
  }

  // Import progress data
  importProgress(data) {
    try {
      if (data.roundProgress) {
        this.scene.roundProgress = data.roundProgress;
        this.saveRoundProgress();
        this.updateRoundsSidebar();
        console.log('📊 Imported progress data:', data);
        return true;
      }
    } catch (error) {
      console.error('Error importing progress:', error);
    }
    return false;
  }
}
